import dayjs from "dayjs";
import { formatMoneyPunto } from "utils/formatters";
import { PERMISOS_COMPRAS, tienePermisoCompras } from "layouts/compras/permisos";

// Tipos de compra. El valor "Concurso" se presenta al usuario como "Proceso de compra".
export const TIPOS_COMPRA = {
  Concurso: {
    etiqueta: "Proceso de compra",
    icon: "shopping_cart_checkout_icon",
    color: "uproGreen",
    procesoCompra: true,
  },
  Directa: {
    etiqueta: "Directa",
    icon: "arrow_outward",
    color: "uproGreen",
    procesoCompra: false,
  },
};

const TAB_DATOS_GENERALES = 0;
const TAB_DETALLES = 1;

const esMismoDia = (a, b) => dayjs(a).isSame(dayjs(b), "day");

// Detalles que se van a persistir (los marcados para eliminar no cuentan).
export const detallesVigentes = (detalles) => (detalles || []).filter((d) => !d?._toRemove);

/**
 * Unidades de la materia prima que trae cada pack según la equivalencia del ítem.
 * Ej: 1 Bidón = 5 lt → 5. Sin equivalencia cargada vale 1.
 */
export const mpPorPack = (item) => {
  const packs = Math.max(1, Number(item?.equivalenciaPack?.packs) || 1);
  const mp = Number(item?.equivalenciaPack?.materiaPrima) || 1;
  return mp / packs;
};

/**
 * Si la materia prima ya está en el pedido sin pack. Una misma materia prima puede
 * pedirse una vez por unidad base y además en pack.
 */
export const yaAgregadaSinPack = (detalles, idMateriaPrima) =>
  detallesVigentes(detalles).some(
    (item) => String(item?.materiaPrima?.id) === String(idMateriaPrima) && !(item?.requierePack && item?.unidadPack)
  );

const nombreItem = (item) =>
  item?.materiaPrima?.nombre || item?.nombreServicio || `#${item?.numero ?? ""}`;

/**
 * Valida el pedido completo (Datos generales + Detalles).
 * Devuelve los errores en el orden en que se muestran al usuario.
 *
 * @param {object} data pedido en edición
 * @param {object} opciones
 * @param {object|null} opciones.original pedido tal como se cargó de la base (null si es nuevo)
 * @param {boolean} opciones.puedeElegirResponsable si el usuario elige el responsable técnico
 * @param {dayjs.Dayjs|Date|string} [opciones.hoy]
 * @returns {{ campo: string, tab: number, mensaje: string }[]}
 */
export const validarPedido = (data, { original = null, puedeElegirResponsable = false, hoy } = {}) => {
  const errores = [];
  const agregar = (campo, tab, mensaje) => errores.push({ campo, tab, mensaje });
  const inicioHoy = dayjs(hoy).startOf("day");

  if (!data?.nombre?.trim()) {
    agregar("nombre", TAB_DATOS_GENERALES, "El nombre es requerido");
  }
  if (!data?.tipoCompra) {
    agregar("tipoCompra", TAB_DATOS_GENERALES, "El tipo de compra es requerido");
  }

  if (!data?.fechaSolicitud) {
    agregar("fechaSolicitud", TAB_DATOS_GENERALES, "La fecha de solicitud es requerida");
  } else {
    // La restricción aplica al cargar la fecha, no a pedidos ya guardados con una fecha pasada.
    const fechaModificada =
      !original?.fechaSolicitud || !esMismoDia(original.fechaSolicitud, data.fechaSolicitud);
    if (fechaModificada && dayjs(data.fechaSolicitud).isBefore(inicioHoy, "day")) {
      agregar("fechaSolicitud", TAB_DATOS_GENERALES, "La fecha de solicitud no puede ser anterior a hoy");
    }
  }

  if (!data?.fechaEntrega) {
    agregar("fechaEntrega", TAB_DATOS_GENERALES, "La fecha de entrega es requerida");
  } else if (data?.fechaSolicitud && dayjs(data.fechaEntrega).isBefore(dayjs(data.fechaSolicitud), "day")) {
    agregar(
      "fechaEntrega",
      TAB_DATOS_GENERALES,
      "La fecha de entrega no puede ser anterior a la fecha de solicitud"
    );
  }

  if (puedeElegirResponsable && !data?.idResponsable && !data?.responsable?.id) {
    agregar("idResponsable", TAB_DATOS_GENERALES, "El responsable de consultas técnicas es requerido");
  }
  if (!data?.objetoCompra?.trim()) {
    agregar("objetoCompra", TAB_DATOS_GENERALES, "El objeto de compra es requerido");
  }

  if (!data?.tipoDetalle) {
    agregar("tipoDetalle", TAB_DETALLES, "El tipo de detalle solicitado es requerido");
  } else if (detallesVigentes(data?.detalles).length === 0) {
    agregar("detalles", TAB_DETALLES, "Debe agregar al menos un elemento");
  } else if (!original || data.detalles !== original.detalles) {
    // Los ítems se validan solo si se modificaron, para no bloquear pedidos guardados antes de estas reglas.
    const itemInvalido = detallesVigentes(data.detalles)
      .map((item) => {
        if (!(Number(item?.cantidad) > 0)) return `El ítem "${nombreItem(item)}" debe tener una cantidad mayor a cero`;
        if (item?.requierePack && !item?.unidadPack?.trim()) {
          return `El ítem "${nombreItem(item)}" se pide en pack: indique la unidad del pack y su equivalencia`;
        }
        return null;
      })
      .find(Boolean);
    if (itemInvalido) agregar("detalles", TAB_DETALLES, itemInvalido);
  }

  if (
    data?.tipoCompra &&
    data.tipoCompra !== "Directa" &&
    !["SI", "NO"].includes(data?.pliegoRequiereDemanda)
  ) {
    agregar(
      "pliegoRequiereDemanda",
      TAB_DETALLES,
      "Seleccione si el pedido requiere o no entregas a demanda"
    );
  }

  return errores;
};

/**
 * Total estimado del pedido. Es el mismo total que se muestra en el tab Detalles.
 */
export const calcularTotalEstimado = (detalles, tipoDetalle) =>
  detallesVigentes(detalles).reduce((acc, item) => {
    const esServicio =
      tipoDetalle === "Servicios" || (!item?.materiaPrima && (item?.nombreServicio || item?.rubroServicio));
    const cantidad = Number(item?.cantidadVerificada ?? item?.cantidad ?? 0) || 0;
    const precio = esServicio
      ? Number(item?.provisionDetalle?.precioUnitario ?? item?.precioEstimado ?? 0) || 0
      : Number(item?.precioEstimado ?? item?.materiaPrima?.precio ?? 0) || 0;
    return acc + cantidad * precio;
  }, 0);

/**
 * Intervalos de monto según el parámetro MONTOS_PRESUPUESTOS:
 * - Directa: de $0 hasta el tope de Directa (inclusive).
 * - Proceso de compra (Concurso): más del tope de Directa, sin tope.
 * El monto de Concurso del parámetro es solo informativo.
 */
const topeDirecta = (montos) => Number(montos?.directa) || 0;

/**
 * Tipo de compra que corresponde a un total, o null si el tope de Directa no está configurado.
 */
export const tipoCompraSegunMonto = (total, montos) => {
  const tope = topeDirecta(montos);
  if (!(tope > 0)) return null;
  return total <= tope ? "Directa" : "Concurso";
};

/**
 * Texto del intervalo de un tipo de compra. Ej: "$0 - $1.800.000" o "más de $1.800.000".
 */
export const describirIntervalo = (tipoCompra, montos) => {
  const tope = topeDirecta(montos);
  if (!(tope > 0)) return null;
  return tipoCompra === "Directa"
    ? `$0 - $${formatMoneyPunto(tope)}`
    : `más de $${formatMoneyPunto(tope)}`;
};

/**
 * Verifica que el total estimado no supere el tope del tipo de compra elegido.
 * Solo Directa tiene tope: un Proceso de compra con un total que entraría en Directa es válido.
 * Devuelve null si no supera el tope o si no hay datos para evaluarlo (sin total o sin tope).
 *
 * @returns {null | { total: number, tipoCorrecto: string }}
 */
export const verificarTipoCompraSegunMonto = (tipoCompra, total, montos) => {
  if (tipoCompra !== "Directa" || !(total > 0)) return null;
  const tipoCorrecto = tipoCompraSegunMonto(total, montos);
  if (!tipoCorrecto || tipoCorrecto === tipoCompra) return null;
  return { total, tipoCorrecto };
};

/**
 * Si el usuario puede editar los datos generales del pedido.
 */
export const puedeEditarDatosGenerales = (data, mode, currentUser) => {
  if (mode === "Lectura") return false;
  if (data?.estados?.length > 1) return false;
  if (data?.estados?.[0]?.finalizado) return false;
  if (!data?.id) return true;
  const esResponsable = [data?.createdUser?.id, data?.responsable?.id, data?.idResponsable]
    .some((id) => id != null && String(id) === String(currentUser?.id));
  const verificarAcceso = (ids) => ids.every((id) =>
    currentUser?.permisos?.some((permiso) => String(permiso.id) === String(id))
  );
  return esResponsable || tienePermisoCompras(currentUser, verificarAcceso, PERMISOS_COMPRAS.EDITAR_PEDIDOS_AJENOS);
};

/**
 * Si el usuario puede editar los ítems del pedido. Además de las reglas de los datos
 * generales, los ítems se bloquean una vez registrada la verificación de stock
 * o el inicio de cotización. Retroceder pasos no deshace el llamado a proveedores.
 */
export const puedeEditarDetalles = (data, mode, currentUser) => {
  if (data?.licitacionInicio) return false;
  if (data?.stockEstado && data.stockEstado !== "Sin registro") return false;
  return puedeEditarDatosGenerales(data, mode, currentUser);
};

/**
 * Si el tipo de compra todavía puede cambiarse. El tipo define el circuito de pasos: una vez
 * registrado un paso (aunque después se haya vuelto atrás) queda fijo. El backend aplica lo mismo.
 */
export const puedeCambiarTipoCompra = (data, { puedeEditar, puedeCrearSolicitud }) =>
  Boolean(puedeEditar) &&
  !data?.licitacionInicio &&
  !(data?.estados?.length > 0) &&
  (Boolean(data?.id) || Boolean(puedeCrearSolicitud));
